import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Plus, Bell, Settings, LogOut, TrendingUp, CheckCircle,
  XCircle, Clock, Send, DollarSign, Users, Plug, Mail
} from "lucide-react";
import TermsAgreementModal from "@/components/TermsAgreementModal";
import Footer from "@/components/Footer";
import Sidebar from "@/components/Sidebar";

const Dashboard = () => {
  const { user, logout, token, updateUser, getCachedData, setCachedData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(() => getCachedData('stats'));
  const [notifications, setNotifications] = useState([]);
  const [recentProposals, setRecentProposals] = useState(() => getCachedData('proposals')?.slice(0, 5) || []);
  const [loading, setLoading] = useState(!getCachedData('stats'));
  const [showGmailPrompt, setShowGmailPrompt] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const welcomeShownRef = useRef(false);
  const initialLoadRef = useRef(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDashboard();
    
    // Check if we need to show Gmail connect prompt
    if (searchParams.get('connect_gmail') === 'true') {
      setShowGmailPrompt(true);
      // Remove the query param from URL
      searchParams.delete('connect_gmail');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Check if user needs to agree to terms
    if (user && !user.terms_accepted) {
      setShowTermsModal(true);
    } else if (user && user.terms_accepted && initialLoadRef.current && !welcomeShownRef.current) {
      // Show welcome back toast ONLY on initial page load after login (not on navigation)
      // Check sessionStorage to see if we already showed toast this session
      const toastShown = sessionStorage.getItem('welcomeToastShown');
      if (!toastShown) {
        welcomeShownRef.current = true;
        sessionStorage.setItem('welcomeToastShown', 'true');
        toast.success("Welcome back!");
      }
    }
    initialLoadRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const connectGmail = () => {
    window.location.href = `${API}/integrations/email/google/authorize`;
  };

  const handleAcceptTerms = async () => {
    try {
      const response = await axios.post(`${API}/auth/accept-terms`, {}, { headers, withCredentials: true });
      updateUser({ ...user, terms_accepted: true, terms_accepted_at: response.data.terms_accepted_at });
      setShowTermsModal(false);
      toast.success("Welcome to ProspectConnect!");
    } catch (error) {
      toast.error("Failed to save your agreement. Please try again.");
    }
  };

  const loadDashboard = async () => {
    // Check cache first
    const cachedStats = getCachedData('stats');
    const cachedProposals = getCachedData('proposals');
    
    if (cachedStats && cachedProposals) {
      setStats(cachedStats);
      setRecentProposals(cachedProposals.slice(0, 5));
      setLoading(false);
      // Still fetch notifications (they change more often)
      try {
        const notifRes = await axios.get(`${API}/notifications`, { headers, withCredentials: true });
        setNotifications(notifRes.data);
      } catch (e) {}
      return;
    }
    
    try {
      const [statsRes, notifRes, proposalsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/notifications`, { headers, withCredentials: true }),
        axios.get(`${API}/proposals?limit=50`, { headers, withCredentials: true })
      ]);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
      setRecentProposals(proposalsRes.data.slice(0, 5));
      // Cache the data
      setCachedData('stats', statsRes.data);
      setCachedData('proposals', proposalsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers, withCredentials: true });
      setNotifications(notifications.map(n => n.notification_id === id ? {...n, read: true} : n));
    } catch (error) {
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { class: "bg-stone text-foreground", icon: Clock },
      sent: { class: "bg-blue-100 text-blue-800", icon: Send },
      accepted: { class: "bg-green-100 text-green-800", icon: CheckCircle },
      declined: { class: "bg-red-100 text-red-800", icon: XCircle }
    };
    const v = variants[status] || variants.draft;
    return (
      <Badge className={`${v.class} capitalize`}>
        <v.icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-sand flex" data-testid="advisor-dashboard">
      {/* Sidebar Component */}
      <Sidebar user={user} logout={logout} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <main className="flex-1 p-4 pt-16 md:pt-4 lg:p-8">
        {/* Gmail Connect Prompt */}
        {showGmailPrompt && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-900">Enable Email Sending</p>
                <p className="text-sm text-amber-700">Connect Gmail to send proposals directly to your prospects.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowGmailPrompt(false)}
                className="text-amber-700 hover:text-amber-900"
              >
                Later
              </Button>
              <Button 
                size="sm"
                onClick={connectGmail}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Connect Gmail
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-forest">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              {user?.terms_accepted ? 'Welcome back' : 'Welcome'}, {user?.name?.split(' ')[0]}
            </p>
          </div>
          <Button asChild className="bg-terracotta hover:bg-terracotta/90" data-testid="create-proposal-btn">
            <Link to="/proposals/new">
              <Plus className="w-4 h-4 mr-2" />
              New Proposal
            </Link>
          </Button>
        </div>

        {/* Primary Stats Grid - Pipeline & Conversion */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <Card className="border-stone">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-label text-sm uppercase tracking-wider text-muted-foreground">Pipeline</p>
                  <p className="font-mono text-3xl font-bold text-amber-600 mt-1">{stats?.pipeline || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Awaiting response</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-label text-sm uppercase tracking-wider text-muted-foreground">Won</p>
                  <p className="font-mono text-3xl font-bold text-green-700 mt-1">{stats?.accepted || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Accepted</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-label text-sm uppercase tracking-wider text-muted-foreground">Lost</p>
                  <p className="font-mono text-3xl font-bold text-red-600 mt-1">{stats?.declined || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Declined</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-label text-sm uppercase tracking-wider text-muted-foreground">Win Rate</p>
                  <p className="font-mono text-3xl font-bold text-forest mt-1">{stats?.acceptance_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Conversion</p>
                </div>
                <div className="w-12 h-12 bg-forest/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-forest" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-label text-sm uppercase tracking-wider text-muted-foreground">Drafts</p>
                  <p className="font-mono text-3xl font-bold text-stone-600 mt-1">{stats?.draft || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Not yet sent</p>
                </div>
                <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-stone-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AUM & Revenue Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* AUM Card */}
          <Card className="border-stone">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-forest" />
                Assets Under Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Proposed</p>
                  <p className="font-mono text-2xl font-bold text-stone-700">
                    ${((stats?.proposed_aum || 0) / 1000000).toFixed(2)}M
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Actual (Won)</p>
                  <p className="font-mono text-2xl font-bold text-green-700">
                    ${((stats?.actual_aum || 0) / 1000000).toFixed(2)}M
                  </p>
                </div>
              </div>
              {stats?.proposed_aum > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${Math.min(stats?.aum_conversion_rate || 0, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-forest">{stats?.aum_conversion_rate || 0}%</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Card */}
          <Card className="border-stone">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-terracotta" />
                Advisory Fees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Proposed</p>
                  <p className="font-mono text-2xl font-bold text-stone-700">
                    ${(stats?.proposed_fees || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-terracotta/10 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Actual (Won)</p>
                  <p className="font-mono text-2xl font-bold text-terracotta">
                    ${(stats?.actual_fees || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              {stats?.proposed_fees > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-terracotta rounded-full transition-all"
                      style={{ width: `${Math.min(stats?.fee_conversion_rate || 0, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-terracotta">{stats?.fee_conversion_rate || 0}%</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Recent Proposals */}
          <div className="lg:col-span-2">
            <Card className="border-stone">
              <CardHeader className="flex flex-row items-center justify-between py-3 lg:py-6">
                <CardTitle className="font-display text-base lg:text-xl">Recent Proposals</CardTitle>
                <Button variant="ghost" asChild className="text-forest text-sm">
                  <Link to="/proposals">View All</Link>
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                {recentProposals.length === 0 ? (
                  <div className="text-center py-8 lg:py-12">
                    <FileText className="w-8 h-8 lg:w-12 lg:h-12 text-muted-foreground mx-auto mb-3 lg:mb-4" />
                    <p className="text-muted-foreground text-sm lg:text-base">No proposals yet</p>
                    <Button asChild className="mt-3 lg:mt-4 bg-forest hover:bg-forest/90 text-sm">
                      <Link to="/proposals/new">Create Your First Proposal</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {recentProposals.map((proposal) => (
                      <Link
                        key={proposal.proposal_id}
                        to={`/proposals/${proposal.proposal_id}`}
                        className="flex items-center justify-between p-3 lg:p-4 rounded-lg border border-stone hover:border-forest/30 transition-colors"
                        data-testid={`proposal-${proposal.proposal_id}`}
                      >
                        <div className="flex items-center gap-3 lg:gap-4">
                          <div className="w-8 h-8 lg:w-10 lg:h-10 bg-sand rounded-lg flex items-center justify-center">
                            <Users className="w-4 h-4 lg:w-5 lg:h-5 text-forest" />
                          </div>
                          <div>
                            <p className="font-medium text-forest text-sm lg:text-base">{proposal.title}</p>
                            <p className="text-xs lg:text-sm text-muted-foreground">{proposal.prospect_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 lg:gap-4">
                          {proposal.total_amount && (
                            <span className="font-mono text-forest text-sm lg:text-base">${proposal.total_amount.toLocaleString()}</span>
                          )}
                          {getStatusBadge(proposal.status)}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Notifications */}
          <div>
            <Card className="border-stone">
              <CardHeader className="flex flex-row items-center justify-between py-3 lg:py-6">
                <CardTitle className="font-display text-base lg:text-xl flex items-center gap-2">
                  <Bell className="w-4 h-4 lg:w-5 lg:h-5" />
                  Notifications
                </CardTitle>
                {stats?.unread_notifications > 0 && (
                  <Badge className="bg-terracotta">{stats.unread_notifications}</Badge>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="h-[250px] lg:h-[400px]">
                  {notifications.length === 0 ? (
                    <div className="text-center py-8 lg:py-12">
                      <Bell className="w-8 h-8 lg:w-12 lg:h-12 text-muted-foreground mx-auto mb-3 lg:mb-4" />
                      <p className="text-muted-foreground text-sm lg:text-base">No notifications</p>
                    </div>
                  ) : (
                    <div className="space-y-2 lg:space-y-3">
                      {notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-3 lg:p-4 rounded-lg border transition-colors cursor-pointer ${
                            notif.read ? 'border-stone bg-white' : 'border-forest/30 bg-forest/5'
                          }`}
                          onClick={() => markNotificationRead(notif.notification_id)}
                        >
                          <div className="flex items-start gap-2 lg:gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 lg:mt-2 ${notif.read ? 'bg-stone' : 'bg-terracotta'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-forest text-xs lg:text-sm">{notif.title}</p>
                              <p className="text-xs lg:text-sm text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                              <p className="text-xs text-muted-foreground mt-1 lg:mt-2">
                                {new Date(notif.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer isLoggedIn={true} />
      </div>
      
      <TermsAgreementModal open={showTermsModal} onAgree={handleAcceptTerms} />
    </div>
  );
};

export default Dashboard;
